import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { webReadGroup } from "../../DataApi/web_read_group";
import { searchRead } from "../../DataApi/search_read";

const initialState = {
  boards: [],
  draggedCard: null,
  loading: "idle",
  error: null,
};

const fetchCRMData = createAsyncThunk(
  "crm/fetchData",
  async ({
    company_id,
    context,
    selectedView,
    startEndDate,
    pagination,
    search = { query: "", type: "" },
    sort = { type: "", dir: "asc" },
  }) => {
    const searchDomain = [];
    if (search.query) {
      searchDomain.push(
        "|",
        "|",
        "|",
        "|",
        ["partner_id", "ilike", search.query],
        ["partner_name", "ilike", search.query],
        ["email_from", "ilike", search.query],
        ["name", "ilike", search.query],
        ["contact_name", "ilike", search.query]
      );
    }

    if ((company_id, context, selectedView === "kanbanView")) {
      try {
        const webReadGroupResponse = await webReadGroup({
          company_id,
          context,
          selectedView,
          search,
          sort,
        });
        const webReadGroupJson = JSON.parse(webReadGroupResponse);
        const groupsKanban = webReadGroupJson.result.groups;

        const boards = {};

        if (Array.isArray(groupsKanban)) {
          for (const grp of groupsKanban) {
            try {
              const searchReadResponse = await searchRead(
                company_id,
                context,
                selectedView,
                grp.__domain,
                "",
                pagination,
                search,
                sort
              );
              const searchData = JSON.parse(searchReadResponse);
              const records = searchData.result.records;

              // Extracting board details
              const boardId = grp.stage_id[0];
              const boardTitle = grp.stage_id[1];

              if (!boards[boardId]) {
                boards[boardId] = {
                  stage_id: boardId,
                  title: boardTitle,
                  cards: [],
                };
              }

              // Extracting card details
              records.forEach((record) => {
                if (record.stage_id[1] === boardTitle) {
                  boards[boardId].cards.push({
                    ...record,
                  });
                }
              });
            } catch (error) {
              console.error("An error occurred in searchRead:", error);
            }
          }
        }
        return Object.values(boards);
      } catch (error) {
        console.error("An error occurred in webReadGroup:", error);
        throw error;
      }
    } else if (selectedView === "taskList") {
      try {
        const searchReadListResponse = await searchRead(
          company_id,
          context,
          selectedView,
          searchDomain,
          null,
          pagination,
          search,
          sort,
          "crm.lead"
        );
        const searchListData = JSON.parse(searchReadListResponse);
        // console.log("CalenderView REsponse", searchListData);

        return {
          records: searchListData?.result?.records || [],
          length: searchListData?.result?.length || 0,
        };
      } catch (error) {
        console.error("An error occurred in searchRead:", error);
      }
    } else if (selectedView === "timeoff") {
      try {
        const searchReadClndrResponse = await searchRead(
          company_id,
          context,
          selectedView,
          null,
          startEndDate
        );
        const searchClndrData = JSON.parse(searchReadClndrResponse);
        // console.log("CalenderView REsponse", searchClndrData);
        return {
          records: searchClndrData?.result?.records || [],
          length: searchClndrData?.result?.length || 0,
        };
      } catch (error) {
        console.error("An error occurred in searchRead:", error);
      }
    }
  }
);

const CrmSlice = createSlice({
  name: "crm",
  initialState,
  reducers: {
    updateBoardTitle: (state, action) => {
      const { stageId, newTitle } = action.payload;

      // Find the index of the board with the given stageId
      const boardIndex = state.boards.findIndex(
        (board) => board.stage_id === stageId
      );

      // If the board is found, update its title
      if (boardIndex !== -1) {
        const updatedBoards = [...state.boards];
        updatedBoards[boardIndex] = {
          ...updatedBoards[boardIndex],
          title: newTitle,
        }; // Update the title of the specific board
        return { ...state, boards: updatedBoards };
      } else {
        console.error("Board with stageId", stageId, "not found.");
        return state;
      }
    },

    addBoard: (state, action) => {
      const {
        result: [stage_id, name],
      } = action.payload;
      const newBoard = {
        stage_id: stage_id,
        title: name,
        cards: [],
      };
      return {
        ...state,
        boards: [...state.boards, newBoard],
      };
    },

    removeBoard: (state, action) => {
      const stageId = action.payload;
      // Filter out the board with the specified stageId
      const updatedBoards = state.boards.filter(
        (board) => board.stage_id !== stageId
      );
      return { ...state, boards: updatedBoards };
    },

    addCard: (state, action) => {
      const { stageId, cardDetails } = action.payload;
      const newCard = { id: Date.now(), ...cardDetails };
      const updatedBoards = state.boards.map((board) =>
        board.stage_id === stageId
          ? { ...board, cards: [...board.cards, newCard] }
          : board
      );
      return { ...state, boards: updatedBoards };
    },
    addCardsList: (state, action) => {
      const { stageId, cardsList } = action.payload;

      const updatedBoards = state.boards.map((board) =>
        board.stage_id === stageId
          ? { ...board, cards: [...board.cards, ...cardsList] }
          : board
      );
      return { ...state, boards: updatedBoards };
    },

    removeCard: (state, action) => {
      const { stageId, cardId } = action.payload;
      const board = state.boards.find((board) => board.stage_id === stageId);

      if (!board) {
        console.error(`Board with stageId ${stageId} not found.`);
        return state;
      }

      board.cards = board.cards.filter((card) => card.id !== cardId);

      return state;
    },

    dragStart: (state, action) => {
      const { stageId, cardId } = action.payload;
      const board = state.boards.find((board) => board.stage_id === stageId);
      const draggedCard = board?.cards.find((card) => card.id === cardId);
      state.draggedCard = draggedCard || null;
    },
    dragEnd: (state) => {
      state.draggedCard = null;
    },
    dragOver: (state, action) => {
      // You may update the state for dragOver if needed
    },
    dropCard: (state, action) => {
      const { stageId, newCard } = action.payload;
      if (stageId === newCard?.stage_id?.[0]) {
        return state;
      }
      if (stageId && newCard) {
        const updatedBoards = state.boards.map((board) => {
          if (board.stage_id === stageId) {
            return {
              ...board,
              cards: [
                ...board.cards,
                { ...newCard, stage_id: [stageId, board?.title] },
              ],
            };
          } else if (board.cards.some((card) => card.id === newCard.id)) {
            return {
              ...board,
              cards: board.cards.filter((card) => card.id !== newCard.id),
            };
          }
          return board;
        });

        return {
          ...state,
          boards: updatedBoards,
          draggedCard: null,
        };
      }

      return state;
    },

    updateCard: (state, action) => {
      const { stageId, cardId, updatedCard } = action.payload;
      const updatedBoards = state.boards.map((board) =>
        board.stage_id === stageId
          ? {
              ...board,
              cards: board.cards.map((card) =>
                card.id === cardId ? { ...card, ...updatedCard } : card
              ),
            }
          : board
      );

      return { ...state, boards: updatedBoards };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCRMData.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchCRMData.fulfilled, (state, action) => {
        state.boards = action.payload;
        state.loading = "succeed";
        state.error = null;
      })
      .addCase(fetchCRMData.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message;
      });
  },
});

export const {
  updateBoardTitle,
  addBoard,
  removeBoard,
  addCard,
  dropCard,
  removeCard,
  dragStart,
  dragEnd,
  dragOver,
  addCardsList,
  updateCard,
} = CrmSlice.actions;
export const CrmAction = {
  ...CrmSlice.actions,
  fetchCRMData,
};

export default CrmSlice.reducer;
